import PropTypes from 'prop-types';
import ActionItem from 'Classes/ActionItem';
import { propTypes as clientPT } from './Client';
import { propTypes as servicePT } from './ClientService';

class Household {
  constructor(id = 0) {
    this.id = id;
    this.csManagerId = 0;
    this.dashboardNotes = '';
    this.escalated = false;
    this.escalationNote = '';
    this.escalationType = '';
    this.leadPlannerId = 0;
    this.paymentMethod = '';
    this.paymentSchedule = 'month';
    this.personalInterests = [];
    this.primaryClientId = 0;
    this.primaryClientFirstName = '';
    this.primaryClientLastName = '';
    this.primaryClientEmail = '';
    this.primaryClientPhoneNumber = '';
    this.addressLine1 = '';
    this.addressLine2 = '';
    this.addressLine3 = '';
    this.city = '';
    this.state = '';
    this.postalCode = '';
    this.maritalStatus = '';
    this.spouseClientFirstName = '';
    this.spouseClientLastName = '';
    this.spouseClientEmail = '';
    this.spouseClientPhoneNumber = '';
    this.serviceLevelId = 0;
    this.sourceId = 0;
    this.sourceTypeId = 0;
    this.sourcePartnerId = 0;
    this.statusId = 0;
    this.timeZone = '';
    this.communicationType = '';
    this.clients = [];
    this.services = [];
    this.hasPaymentToken = false;
    this.status = '';
    this.accounts = {
      liabilities: [],
      realAssets: [],
      investableAssets: [],
    };
    this.billingRate = '';
    this.interval = '';
    this.intervalCount = '';
    this.folderId = 0;
    this.relationships = {};
    this.assetsUnderManagement = '';
    this.churnCategory = '';
    this.churnSubCategory = '';
    this.churnDescription = '';
    this.churnDate = '';
    this.paymentStatus = '';
    this.paymentErrorMessage = '';
    this.cardLastFour = '';
    this.paymentAttemptedOn = '';
    this.paymentAmountRemaining = '';
    this.nextBillingDate = '';
  }
}

export default Household;

export const propTypes = PropTypes.shape({
  id: PropTypes.number.isRequired,
  csManagerId: PropTypes.number.isRequired,
  escalated: PropTypes.bool.isRequired,
  escalationType: PropTypes.string.isRequired,
  escalationNote: PropTypes.string.isRequired,
  leadPlannerId: PropTypes.number.isRequired,
  paymentMethod: PropTypes.string.isRequired,
  paymentSchedule: PropTypes.string.isRequired,
  primaryClientId: PropTypes.number.isRequired,
  primaryClientFirstName: PropTypes.string.isRequired,
  primaryClientLastName: PropTypes.string.isRequired,
  primaryClientEmail: PropTypes.string.isRequired,
  primaryClientPhoneNumber: PropTypes.string,
  addressLine1: PropTypes.string,
  addressLine2: PropTypes.string,
  addressLine3: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  postalCode: PropTypes.string,
  maritalStatus: PropTypes.string.isRequired,
  spouseClientFirstName: PropTypes.string,
  spouseClientLastName: PropTypes.string,
  spouseClientEmail: PropTypes.string,
  spouseClientPhoneNumber: PropTypes.string,
  serviceLevelId: PropTypes.number,
  sourceTypeId: PropTypes.number.isRequired,
  sourcePartnerId: PropTypes.number,
  sourceId: PropTypes.number,
  statusId: PropTypes.number,
  communicationType: PropTypes.string,
  dashboardNotes: PropTypes.string.isRequired,
  timeZone: PropTypes.string.isRequired,
  clients: PropTypes.arrayOf(clientPT),
  services: PropTypes.arrayOf(servicePT),
  folderId: PropTypes.number,
  accounts: PropTypes.shape({
    liabilities: PropTypes.array,
    realAssets: PropTypes.array,
    investableAssets: PropTypes.array,
  }),
  billingRate: PropTypes.string,
  interval: PropTypes.string,
  intervalCount: PropTypes.string,
  assetsUnderManagement: PropTypes.string,
  relationships: PropTypes.object,
  churnCategory: PropTypes.string,
  churnSubCategory: PropTypes.string,
  churnDescription: PropTypes.string,
  churnDate: PropTypes.string,
  paymentStatus: PropTypes.string,
  paymentErrorMessage: PropTypes.string,
  cardLastFour: PropTypes.string,
  paymentAttemptedOn: PropTypes.string,
  paymentAmountRemaining: PropTypes.string,
  nextBillingDate: PropTypes.string,
});

export const convertResponse = response => Object.assign({}, response.attributes, {
  id: response.id,
  leadPlannerId: response.attributes.leadPlannerId,
  csManagerId: response.attributes.csManagerId,
  primaryClientId: response.attributes.primaryClientId,
  primaryClientFirstName: response.attributes.primaryClientFirstName,
  primaryClientLastName: response.attributes.primaryClientLastName,
  status: response.attributes.status,
  maritalStatus: response.attributes.maritalStatus,
  actionItems: response.attributes.actionItems.map(ai => ActionItem.fromAPIResponse(ai)),
  paymentStatus: response.attributes.paymentStatus,
  paymentErrorMessage: response.attributes.paymentErrorMessage,
  cardLastFour: response.attributes.cardLastFour,
  paymentAttemptedOn: response.attributes.paymentAttemptedOn,
  paymentAmountRemaining: response.attributes.paymentAmountRemaining,
  paymentMethod: response.attributes.paymentMethod,
  escalationType: response.attributes.escalationType,
  clientStage: response.attributes.clientStage,
  folderId: response.attributes.folderId,
  churnCategory: response.attributes.churnCategory,
  churnSubCategory: response.attributes.churnSubCategory,
  churnDescription: response.attributes.churnDescription,
  churnDate: response.attributes.churnDate,
  nextBillingDate: response.attributes.nextBillingDate,
});

export const fromAPIResponse = res => ({
  id: res.data.id,
  relationships: res.data.relationships,
  ...res.data.attributes,
});

export const toAPIRequest = req => ({
  id: req.id,
  type: 'household',
  attributes: Object.assign({}, req, {
    leadPlannerId: req.leadPlannerId.toString(),
    csManagerId: req.csManagerId.toString(),
    sourceId: req.sourceId.toString(),
    sourceTypeId: req.sourceTypeId.toString(),
    statusId: req.statusId.toString(),
    sourcePartnerId: (req.sourcePartnerId || '').toString(),
    churnCategory: req.churnCategory || '',
    churnSubCategory: req.churnSubCategory || '',
    churnDescription: req.churnDescription || '',
    churnDate: req.churnDate || '',
  }),
});

export const ONBOARDING = 'Onboarding';
export const PLANNING = 'Planning';
export const SERVICE = 'Service';
export const ACTIVE = 'Active';
export const CHURNED = 'Churned';
export const PROSPECT = 'Prospect';
export const OTHER = 'Other';
const TRANSITION = 'Transition';

export const isProspect = household => household.statusName === PROSPECT || household.status === PROSPECT;

export const isActiveOrProspect = household => household.statusName === PROSPECT
  || household.statusName === ACTIVE
  || household.status === PROSPECT
  || household.status === ACTIVE;

export const isOnboarding = household => (household.clientStage === ONBOARDING
    || household.clientStage === TRANSITION)
  && isProspect(household);

export const isActive = household => household.statusName === ACTIVE || household.status === ACTIVE;

export const isEscalated = household => household
  && (household.escalated === true || household.escalated === 'true')
  && isActiveOrProspect(household);

export const isInPlanning = household => household.clientStage === PLANNING && isActive(household);

export const isInService = household => household.clientStage === SERVICE && isActive(household);
